// commerceLogoService — Plan B: el logo del comercio para impresión Web Bluetooth se obtiene
// del backend en base64 (data: URI), NO de la URL del CDN (commerce.logotipo). La URL del CDN
// está bloqueada por CORS → al rasterizarla en canvas, getImageData lanza (taint) y el logo se
// omite en silencio. El data: URI base64 NO taintea el canvas → getImageData funciona → se
// rasteriza e imprime.
//
// Servicio PLANO (no-hook): corre en el print path (fuera de React) y también lo envuelve un
// hook para el preview. axiosInstance ya estampa Authorization + id_comercio (ver
// featureVersionService.ts), así que el endpoint resuelve el comercio del JWT/header.

import axiosInstance from './axiosInstance';

// Llave dedicada de localStorage. NO la borra resetStore(login) (patrón PR#1020): el reset
// limpia el store en memoria, no esta llave; así el logo descargado sobrevive logout/login y
// recargas. Mapa keyado por commerceId (multisucursal: dos comercios no colisionan).
const STORAGE_KEY = 'webBleCommerceLogo';

// Entrada cacheada: el data: URI del logo + la URL del CDN con la que se descargó (para
// invalidar cuando el comercio sube un logo nuevo → commerce.logotipo cambia).
type CachedLogo = { logotipoUrl: string; dataUri: string };
type LogoCacheMap = Record<string, CachedLogo>; // { [commerceId]: { logotipoUrl, dataUri } }

// Respuesta del endpoint (contrato backend GET /v1/comercio/logo).
type CommerceLogoResponse = {
  success: boolean;
  data?: { base64: string; contentType: string; dataUri: string };
};

// Lee el mapa de cache de localStorage. Tolerante: JSON corrupto / sin localStorage → {}.
function readCacheMap(): LogoCacheMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as LogoCacheMap) : {};
  } catch {
    return {};
  }
}

// Persiste el mapa de cache. Tolerante: si localStorage falla (quota / modo privado) → no-op.
function writeCacheMap(map: LogoCacheMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // no-op: el cache es best-effort; sin persistencia se re-descarga la próxima vez.
  }
}

// GET /v1/comercio/logo — privado (axiosInstance estampa auth + id_comercio). Devuelve el
// data: URI base64 del logo, o null en CUALQUIER fallo (404 sin logo / 502 fallo de descarga /
// 401 auth / red). NUNCA lanza: el logo es best-effort y no debe romper la impresión.
export async function fetchCommerceLogoDataUri(): Promise<string | null> {
  try {
    const { data } = await axiosInstance.get<CommerceLogoResponse>('/v1/comercio/logo');
    return data?.success && data.data?.dataUri ? data.data.dataUri : null;
  } catch {
    // 404/502/401 caen aquí (axios lanza en ≥400) → null = "sin logo".
    return null;
  }
}

// Resuelve el data: URI del logo con cache en localStorage por commerceId, invalidando por
// logotipoUrl: si la URL del CDN cambió respecto a la cacheada (logo nuevo) → re-fetch. Si
// !logotipoUrl → null (sin logo, NO llama al endpoint). Si !commerceId → null.
export async function getCommerceLogoDataUri(
  commerceId: string | number | null | undefined,
  logotipoUrl: string | null | undefined,
): Promise<string | null> {
  // Sin comercio o sin logo configurado: no toca el endpoint ni la cache.
  if (!commerceId || !logotipoUrl) return null;

  const key = String(commerceId);
  const map = readCacheMap();
  const cached = map[key];

  // Hit: misma logotipoUrl que la cacheada → devolver el data: URI sin fetch.
  if (cached && cached.logotipoUrl === logotipoUrl) return cached.dataUri;

  // Miss o logotipoUrl distinto (logo cambiado) → re-fetch del endpoint base64.
  const dataUri = await fetchCommerceLogoDataUri();
  if (!dataUri) return null; // no cachear el fallo: se reintenta la próxima vez.

  map[key] = { logotipoUrl, dataUri };
  writeCacheMap(map);
  return dataUri;
}
